import React from 'react';
import PropTypes from 'prop-types';

import images from 'react-payment-inputs/images';

import cvvIcon from '../../../../assets/images/pro/cvv_icon.png';

import OutlinedTextField from '../textFields/OutlinedTextField';
import OutlinedAutocomplete from '../textFields/OutlinedAutocomplete';
import { getMonths, getYears } from '../../../utils/form_validaters/payment_form_helper';

import formStyles from '../styles/styles.module.scss';
import styles from './styles/styles.module.scss';
const CardValidator = require('card-validator');

const CreditCardForm = ({ text, errors, changeHandler, submitHandler, name, number, month, year, cvv, postalCode }) => {
  const cardInfo = CardValidator.number(number);

  const getFormattedCardNumber = cardNumber => {
    const response = [];
    if (cardInfo.card && cardInfo.card.lengths) {
      const numberAsArray = cardNumber.split('');
      const maxDigits = 20;
      const limit = numberAsArray.length < maxDigits ? numberAsArray.length : maxDigits - 1;
      for (let i = 0; i < limit; i++) {
        if (cardInfo.card.gaps.indexOf(i) !== -1) {
          response.push(' ');
        }
        if (!isNaN(numberAsArray[i])) {
          response.push(numberAsArray[i]);
        }
      }
    } else {
      return cardNumber;
    }
    return response.join('');
  };

  const cardNumberChangeHandler = event => {
    const processedEvent = event;
    const { value } = processedEvent.target;
    processedEvent.target.value = value.replace(/\D/g, '');
    changeHandler(processedEvent);
  };

  const getFormattedCVVCode = cvvCode => {
    const response = [];
    let maxLength = 3;

    if (cvvCode) {
      const cvvArray = cvvCode.split('');
      if (cardInfo.card && cardInfo.card.code) {
        maxLength = cardInfo.card.code.size;
      }
      for (let i = 0; i < cvvArray.length; i++) {
        if (i < maxLength) {
          response.push(cvvCode[i].replace(/\D/g, ''));
        }
      }
    }
    return response.join('');
  };

  const renderCardImage = (cardImage, styleKey) => {
    return <svg className={`${styles.cardIcon} ${styles[styleKey]}`}>{cardImage}</svg>;
  };

  const getCardTypeSVG = () => {
    const acceptedCards = ['amex', 'visa', 'mastercard', 'discover'];
    const cardImages = [];

    acceptedCards.map(cardType => {
      const cardImage = images[cardType];
      let styleKey = '';
      if (cardInfo.card) {
        let selectedCardType = cardInfo.card.type;

        if (selectedCardType == 'american-express') {
          selectedCardType = 'amex'; // annoying mismatch in type name for amex
        }

        if (selectedCardType === cardType) {
          styleKey = 'active';
        } else {
          styleKey = 'inactive';
        }
      }

      return cardImages.push(renderCardImage(cardImage, styleKey));
    });

    return cardImages;
  };

  return (
    <div>
      <div className={formStyles.row}>
        <OutlinedTextField
          name="name"
          autocomplete="cc-name"
          label={text.name.title}
          isError={errors.name.length > 0}
          errorMessage={errors.name}
          value={name}
          changeHandler={changeHandler}
          enterKeyHandler={submitHandler}
        />
      </div>
      <div className={formStyles.row}>
        <OutlinedTextField
          name="number"
          autocomplete="cc-number"
          label={text.number.title}
          isError={errors.number.length > 0}
          adornment={getCardTypeSVG()}
          errorMessage={errors.number}
          value={getFormattedCardNumber(number)}
          changeHandler={cardNumberChangeHandler}
          enterKeyHandler={submitHandler}
        />
      </div>
      <div className={formStyles.row}>
        <div className={formStyles.col}>
          <OutlinedAutocomplete
            name="cc-exp-month"
            autocomplete="cc-exp-month"
            options={getMonths()}
            label={text.month.title}
            isError={errors['cc-exp-month'].length > 0}
            errorMessage={errors['cc-exp-month']}
            value={month}
            changeHandler={changeHandler}
            enterKeyHandler={submitHandler}
          />
        </div>
        <div className={formStyles.col}>
          <OutlinedAutocomplete
            options={getYears()}
            name="cc-exp-year"
            autocomplete="cc-exp-year"
            label={text.year.title}
            isError={errors['cc-exp-year'].length > 0}
            errorMessage={errors['cc-exp-year']}
            value={year}
            changeHandler={changeHandler}
            enterKeyHandler={submitHandler}
          />
        </div>
      </div>
      <div className={formStyles.row}>
        <div className={formStyles.col}>
          <OutlinedTextField
            name="cvv"
            autocomplete="cc-csc"
            label={text.cvv.title}
            adornment={<img alt="cvv code" className={styles.cardIcon} src={cvvIcon} />}
            isError={errors.cvv.length > 0}
            errorMessage={errors.cvv}
            value={getFormattedCVVCode(cvv)}
            changeHandler={changeHandler}
            enterKeyHandler={submitHandler}
          />
        </div>
        <div className={formStyles.col}>
          <OutlinedTextField
            name="postalCode"
            autocomplete="postal-code"
            label={text.postalCode.title}
            isError={errors.postalCode.length > 0}
            errorMessage={errors.postalCode}
            value={postalCode}
            changeHandler={changeHandler}
            enterKeyHandler={submitHandler}
          />
        </div>
      </div>
    </div>
  );
};

CreditCardForm.defaultProps = {
  changeHandler: () => {},
  submitHandler: () => {},
  errors: {
    name: '',
    number: '',
    'cc-exp-month': '',
    'cc-exp-year': '',
    cvv: '',
    postalCode: ''
  },
  name: '',
  number: '',
  month: '',
  year: '',
  cvv: '',
  postalCode: ''
};

CreditCardForm.propTypes = {
  text: PropTypes.shape({
    name: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    number: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    month: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    year: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    cvv: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    }),
    postalCode: PropTypes.shape({
      title: PropTypes.string,
      validation: PropTypes.shape({
        generic: PropTypes.string
      })
    })
  }).isRequired,
  errors: PropTypes.shape({
    name: PropTypes.string,
    number: PropTypes.string,
    'cc-exp-month': PropTypes.string,
    'cc-exp-year': PropTypes.string,
    cvv: PropTypes.string,
    postalCode: PropTypes.string
  }),
  changeHandler: PropTypes.func,
  submitHandler: PropTypes.func,
  name: PropTypes.string,
  number: PropTypes.string,
  month: PropTypes.shape({
    string: PropTypes.string,
    title: PropTypes.string,
    value: PropTypes.number
  }),
  year: PropTypes.shape({
    string: PropTypes.string,
    value: PropTypes.number
  }),
  cvv: PropTypes.string,
  postalCode: PropTypes.string
};

export default CreditCardForm;
